import React, { useState, useRef, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Pressable,
  Platform,
} from 'react-native';
import ReactDOM from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useResponsive } from '../../hooks/useResponsive';
import { FONTS } from '../../constants/config';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon } from '../Icons';
import { createShadow } from '../../utils/styles';
import { CalendarView } from './types';

export interface DateRange {
  start: Date;
  end: Date;
}

interface CalendarHeaderProps {
  dateRangeLabel: string;
  activeView: CalendarView;
  onViewChange: (view: CalendarView) => void;
  onToday: () => void;
  onPrev: () => void;
  onNext: () => void;
  onSelectRange: (range: DateRange) => void;
  onNewActivity: () => void;
  hideNewButton?: boolean;
  titleOverride?: string;
}

const VIEW_TABS: { key: CalendarView; arLabel: string; enLabel: string }[] = [
  { key: 'daily', arLabel: 'يومي', enLabel: 'Daily' },
  { key: 'weekly', arLabel: 'أسبوعي', enLabel: 'Weekly' },
  { key: 'monthly', arLabel: 'شهري', enLabel: 'Monthly' },
];

const MONTH_NAMES_AR = [
  'يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو',
  'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر',
];
const MONTH_NAMES_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];
const DAY_HEADERS_AR = ['إث', 'ثل', 'أر', 'خم', 'جم', 'سب', 'أح'];
const DAY_HEADERS_EN = ['Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa', 'Su'];

function isSameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function isInRange(day: Date, start: Date, end: Date): boolean {
  const t = day.getTime();
  return t >= start.getTime() && t <= end.getTime();
}

function getPickerGridStart(year: number, month: number): Date {
  const firstDay = new Date(year, month, 1);
  const day = firstDay.getDay();
  const diff = day === 0 ? -6 : 1 - day;
  const d = new Date(firstDay);
  d.setDate(firstDay.getDate() + diff);
  return d;
}

function buildPickerWeeks(year: number, month: number): Date[][] {
  const gridStart = getPickerGridStart(year, month);
  const weeks: Date[][] = [];
  const current = new Date(gridStart);
  for (let w = 0; w < 6; w++) {
    const week: Date[] = [];
    for (let d = 0; d < 7; d++) {
      week.push(new Date(current));
      current.setDate(current.getDate() + 1);
    }
    weeks.push(week);
    if (current.getMonth() !== month && current.getDay() === 1) break;
  }
  return weeks;
}

/** Custom calendar dropdown with range selection (2 clicks) */
function DatePickerDropdown({
  onSelect,
  isRTL,
}: {
  onSelect: (range: DateRange) => void;
  isRTL: boolean;
}) {
  const { colors } = useTheme();
  const todayRef = useRef(new Date());
  const today = todayRef.current;
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [rangeStart, setRangeStart] = useState<Date | null>(null);

  const weeks = buildPickerWeeks(viewYear, viewMonth);
  const monthNames = isRTL ? MONTH_NAMES_AR : MONTH_NAMES_EN;
  const dayHeaders = isRTL ? DAY_HEADERS_AR : DAY_HEADERS_EN;

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(viewYear - 1); }
    else setViewMonth(viewMonth - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(viewYear + 1); }
    else setViewMonth(viewMonth + 1);
  };

  const handleDayPress = (day: Date) => {
    if (!rangeStart) {
      // First click — set start
      setRangeStart(new Date(day.getFullYear(), day.getMonth(), day.getDate()));
    } else {
      // Second click — set end, ensure start <= end
      const end = new Date(day.getFullYear(), day.getMonth(), day.getDate());
      const start = rangeStart;
      if (end.getTime() >= start.getTime()) {
        onSelect({ start, end });
      } else {
        onSelect({ start: end, end: start });
      }
    }
  };

  return (
    <View style={[pickerStyles.container, { backgroundColor: colors.card, borderColor: colors.borderLight, ...createShadow(0, 4, 16, '#000', 0.15) }]}>
      {/* Instruction text */}
      {rangeStart && (
        <View style={[pickerStyles.instruction, { backgroundColor: `${colors.secondary}15` }]}>
          <Text style={[pickerStyles.instructionText, { color: colors.secondary }]}>
            {isRTL ? 'اختر تاريخ النهاية' : 'Select end date'}
          </Text>
        </View>
      )}

      {/* Month nav */}
      <View style={pickerStyles.monthNav}>
        <TouchableOpacity onPress={prevMonth} style={pickerStyles.navBtn}>
          <ChevronLeftIcon size={16} color={colors.text} />
        </TouchableOpacity>
        <Text style={[pickerStyles.monthLabel, { color: colors.text }]}>
          {monthNames[viewMonth]} {viewYear}
        </Text>
        <TouchableOpacity onPress={nextMonth} style={pickerStyles.navBtn}>
          <ChevronRightIcon size={16} color={colors.text} />
        </TouchableOpacity>
      </View>

      {/* Day headers */}
      <View style={pickerStyles.dayHeaderRow}>
        {dayHeaders.map((h) => (
          <Text key={h} style={[pickerStyles.dayHeaderText, { color: colors.textMuted }]}>{h}</Text>
        ))}
      </View>

      {/* Weeks */}
      {weeks.map((week, wi) => (
        <View key={wi} style={pickerStyles.weekRow}>
          {week.map((day) => {
            const isCurrentMonth = day.getMonth() === viewMonth;
            const isToday = isSameDay(day, today);
            const isStart = rangeStart ? isSameDay(day, rangeStart) : false;
            const inRange = rangeStart && !isStart
              ? day.getTime() >= rangeStart.getTime()
              : false;

            return (
              <TouchableOpacity
                key={day.toISOString()}
                onPress={() => handleDayPress(day)}
                style={[
                  pickerStyles.dayCell,
                  isToday && !isStart && { backgroundColor: `${colors.secondary}20` },
                  isStart && { backgroundColor: colors.secondary },
                  inRange && { backgroundColor: `${colors.secondary}12` },
                ]}
              >
                <Text style={[
                  pickerStyles.dayText,
                  {
                    color: isStart
                      ? '#FFFFFF'
                      : isCurrentMonth
                        ? colors.text
                        : colors.textLight,
                  },
                  isToday && !isStart && { fontFamily: FONTS.medium },
                ]}>
                  {day.getDate()}
                </Text>
              </TouchableOpacity>
            );
          })}
        </View>
      ))}
    </View>
  );
}

export default function CalendarHeader({
  dateRangeLabel,
  activeView,
  onViewChange,
  onToday,
  onPrev,
  onNext,
  onSelectRange,
  onNewActivity,
  hideNewButton = false,
  titleOverride,
}: CalendarHeaderProps) {
  const { colors } = useTheme();
  const { t, language } = useLanguage();
  const isRTL = language === 'ar';
  const { isMobile } = useResponsive();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerLayout, setPickerLayout] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const pickerBtnRef = useRef<View>(null);

  const handleRangeSelect = useCallback((range: DateRange) => {
    onSelectRange(range);
    setPickerOpen(false);
  }, [onSelectRange]);

  const handleOpenPicker = useCallback(() => {
    if (pickerOpen) {
      setPickerOpen(false);
      return;
    }
    pickerBtnRef.current?.measureInWindow((x: number, y: number, width: number, height: number) => {
      setPickerLayout({ x, y, width, height });
      setPickerOpen(true);
    });
  }, [pickerOpen]);

  return (
    <View style={[styles.container, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
      {/* Title side (RIGHT in RTL) */}
      <View style={[styles.startGroup, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
        <View style={styles.titleGroup}>
          <Text style={[styles.title, { color: colors.primary }]}>
            {titleOverride || t('activityCalendar.title')}
          </Text>
          <Text style={[styles.dateRange, { color: colors.textMuted }]}>
            {dateRangeLabel}
          </Text>
        </View>

        {!isMobile && (
          <View
            style={[
              styles.tabGroup,
              { backgroundColor: colors.background, borderColor: colors.borderLight },
            ]}
          >
            {VIEW_TABS.map((tab) => {
              const isActive = activeView === tab.key;
              return (
                <TouchableOpacity
                  key={tab.key}
                  onPress={() => onViewChange(tab.key)}
                  style={[
                    styles.tab,
                    isActive && [
                      styles.tabActive,
                      {
                        backgroundColor: colors.card,
                        borderColor: colors.borderLight,
                        ...createShadow(0, 1, 3, '#000', 0.08),
                      },
                    ],
                  ]}
                  accessibilityRole="tab"
                  accessibilityState={{ selected: isActive }}
                >
                  <Text
                    style={[
                      styles.tabText,
                      {
                        color: isActive ? colors.text : colors.textMuted,
                        fontFamily: isActive ? FONTS.medium : FONTS.regular,
                      },
                    ]}
                  >
                    {isRTL ? tab.arLabel : tab.enLabel}
                  </Text>
                </TouchableOpacity>
              );
            })}
          </View>
        )}
      </View>

      {/* Actions side (LEFT in RTL) */}
      <View style={[styles.actionsGroup, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
        <View style={[styles.navGroup, { flexDirection: isRTL ? 'row-reverse' : 'row' }]}>
          <TouchableOpacity onPress={onPrev} style={styles.navButton}>
            <ChevronRightIcon size={18} color={colors.textMuted} />
          </TouchableOpacity>
          <TouchableOpacity onPress={onToday} style={styles.todayButton}>
            <Text style={[styles.todayText, { color: colors.text }]}>
              {t('activityCalendar.today')}
            </Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={onNext} style={styles.navButton}>
            <ChevronLeftIcon size={18} color={colors.textMuted} />
          </TouchableOpacity>
        </View>

        {!isMobile && (
          <View style={styles.datePickerWrapper} ref={pickerBtnRef}>
            <TouchableOpacity
              onPress={handleOpenPicker}
              style={[styles.outlinedButton, { borderColor: colors.borderLight }]}
              activeOpacity={0.7}
            >
              <CalendarIcon size={14} color={colors.textMuted} />
              <Text style={[styles.outlinedButtonText, { color: colors.text }]}>
                {t('activityCalendar.selectDate')}
              </Text>
            </TouchableOpacity>

            {pickerOpen && Platform.OS === 'web' && ReactDOM.createPortal(
              <>
                <div
                  style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 99998 }}
                  onClick={() => setPickerOpen(false)}
                />
                <div style={{
                  position: 'fixed',
                  top: pickerLayout.y + pickerLayout.height + 6,
                  left: Math.max(8, pickerLayout.x + pickerLayout.width - 280),
                  zIndex: 99999,
                }}>
                  <DatePickerDropdown
                    onSelect={handleRangeSelect}
                    isRTL={isRTL}
                  />
                </div>
              </>,
              document.body,
            )}
          </View>
        )}

        {!isMobile && !hideNewButton && (
          <TouchableOpacity
            onPress={onNewActivity}
            style={[styles.primaryButton, { backgroundColor: '#a9211f' }]}
          >
            <PlusIcon size={14} color="#FFFFFF" />
            <Text style={styles.primaryButtonText}>
              {t('activityCalendar.newActivity')}
            </Text>
          </TouchableOpacity>
        )}
      </View>

      {/* Backdrop handled by portal */}
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: 12,
    paddingBottom: 24,
  },
  startGroup: { alignItems: 'center', gap: 16 },
  titleGroup: { gap: 2 },
  title: { fontSize: 16, fontFamily: FONTS.medium, lineHeight: 22 },
  dateRange: { fontSize: 12, fontFamily: FONTS.regular, lineHeight: 16 },
  tabGroup: { flexDirection: 'row', borderRadius: 8, borderWidth: 1, padding: 2 },
  tab: { paddingHorizontal: 14, paddingVertical: 5, borderRadius: 6, borderWidth: 1, borderColor: 'transparent' },
  tabActive: { borderWidth: 1 },
  tabText: { fontSize: 13, lineHeight: 18 },
  actionsGroup: { alignItems: 'center', gap: 8, zIndex: 10 },
  navGroup: { alignItems: 'center', gap: 2 },
  navButton: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  todayButton: { paddingHorizontal: 10, paddingVertical: 4 },
  todayText: { fontSize: 13, fontFamily: FONTS.regular, lineHeight: 18 },
  datePickerWrapper: { position: 'relative', zIndex: 20 },
  outlinedButton: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 10, paddingVertical: 5, borderRadius: 8, borderWidth: 1 },
  outlinedButtonText: { fontSize: 13, fontFamily: FONTS.regular, lineHeight: 18 },
  primaryButton: { flexDirection: 'row', alignItems: 'center', gap: 6, paddingHorizontal: 12, paddingVertical: 6, borderRadius: 8 },
  primaryButtonText: { fontSize: 13, fontFamily: FONTS.medium, lineHeight: 18, color: '#FFFFFF' },
});

const pickerStyles = StyleSheet.create({
  container: {
    width: 280,
    borderRadius: 12,
    borderWidth: 1,
    padding: 12,
  },
  instruction: {
    borderRadius: 6,
    paddingVertical: 4,
    paddingHorizontal: 8,
    marginBottom: 8,
    alignItems: 'center',
  },
  instructionText: {
    fontSize: 11,
    fontFamily: FONTS.medium,
    lineHeight: 16,
  },
  monthNav: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 },
  navBtn: { width: 28, height: 28, borderRadius: 14, alignItems: 'center', justifyContent: 'center' },
  monthLabel: { fontSize: 14, fontFamily: FONTS.medium, lineHeight: 20 },
  dayHeaderRow: { flexDirection: 'row', marginBottom: 4 },
  dayHeaderText: { flex: 1, textAlign: 'center', fontSize: 11, fontFamily: FONTS.medium, lineHeight: 16 },
  weekRow: { flexDirection: 'row' },
  dayCell: { flex: 1, alignItems: 'center', justifyContent: 'center', paddingVertical: 6, borderRadius: 16 },
  dayText: { fontSize: 13, fontFamily: FONTS.regular, lineHeight: 18 },
});
